import React, {useEffect, useState} from 'react';
import moment from 'moment';
import {useDispatch, useSelector} from 'react-redux';
import {useSwiper} from 'swiper/react';
import {
  AppDatePicker,
  Row,
  UiBox,
  UiButton,
  UiDropdown,
  UiFormControl,
  UiFormLabel,
  UiImage,
  UiText,
} from '~/components/elements';
import DatePickerCustomInput from '~/components/shared/DatePickerCustomInput';
import {useInput} from '~/hooks';
import {convertDMYToYMD, DATE_FORMAT, formatDate, isUnderAge, RESERVATION_DATE_FORMAT} from '~/utils/helpers';
import {RootState} from '~/redux/root-reducer';
import {DOB_KEY, POP_UP_PH_CHECKOUT_KEYS} from '~/constants/popup';
import {BackAndNextSlide, PopupLayout, TitlePopup} from '~/pages/homes/pop-up/components';
import {setQuestionnaireAnswer} from '~/redux/questionnaires/slice';
import {PopupSorry} from '~/pages/homes/pop-up/PopupSorry';
import {useLocation} from 'react-router-dom';
import CustomDropdown from '~/components/dropdowns/CustomDropdown';
import {SEX_OPTIONS, SEX_VALUES} from '~/constants/medical-questionnaires';
import {ChakraStylesConfig} from 'chakra-react-select';
const queryString = require('query-string');

const chakraStyles: ChakraStylesConfig = {
  control: (provided: any, state: any) => ({
    ...provided,
    minWidth: '10em',
    fontWeight: 'normal',
    textTransform: 'none',
    height: '48px',
    borderRadius: '8px',
  }),
  menuList: (provided: any, state: any) => ({
    ...provided,
    fontWeight: 'normal',
    textTransform: 'none',
    overflowX: 'hidden',
    maxHeight: '200px',
  }),
};

export const PopupDOB = () => {
  const dispatch = useDispatch();
  const swiper = useSwiper();
  const slideKey = useSelector((state: RootState) => state.questionnaires.nextSlideKey);
  const answer = useSelector((state: RootState) => state.questionnaires.collectData);
  const questionnaire = answer?.[POP_UP_PH_CHECKOUT_KEYS.PROFILE];
  const {dateOfBirth, sex} = questionnaire?.data?.user || {};
  const [isValidDOB, setIsValidDOB] = useState(true);
  const medicalQuestionnaire = useSelector((state: RootState) => state.questionnaires.medicalQuestionnaire);
  const {wpAuthUser} = medicalQuestionnaire;
  const location = useLocation();
  const query = queryString.parse(location.search);
  const {mdqId} = query;

  const convertYMDFormat = moment(wpAuthUser?.dateOfBirth, DATE_FORMAT, true).isValid()
    ? wpAuthUser?.dateOfBirth
    : convertDMYToYMD(wpAuthUser?.dateOfBirth);

  const {inputs, setInputs} = useInput({
    dateOfBirth: !!mdqId ? dateOfBirth : convertYMDFormat,
    sex: !!mdqId ? sex : undefined,
  });

  const isNextSlide = !!inputs.dateOfBirth && !!inputs.sex;

  useEffect(() => {
    if (slideKey === DOB_KEY && !!inputs.dateOfBirth) {
      swiper.allowSlideNext = true;
    }
  }, [inputs.dateOfBirth, slideKey, swiper]);

  const handleNextSlide = () => {
    dispatch(
      setQuestionnaireAnswer({
        key: POP_UP_PH_CHECKOUT_KEYS.PROFILE,
        index: 'DEFAULT',
        data: {
          user: !!wpAuthUser
            ? {...wpAuthUser, dateOfBirth: inputs.dateOfBirth, sex: inputs.sex}
            : {...answer?.PROFILE?.data?.user, dateOfBirth: inputs.dateOfBirth, sex: inputs.sex},
        },
      }),
    );
    if (!isUnderAge(inputs.dateOfBirth) && inputs.sex !== SEX_VALUES.male) {
      swiper.enable();
      swiper.slideNext();
    } else {
      swiper.disable();
      setIsValidDOB(false);
    }
  };

  const handleGoBack = () => {
    setIsValidDOB(true);
  };

  return (
    <>
      {!isValidDOB ? (
        <>
          <Row justifyContent="flex-start" flex={1}>
            <UiButton
              onClick={handleGoBack}
              variant="nonFocus"
              style={{display: 'flex', alignItems: 'center'}}
              leftIcon={<UiImage type="icon" name="chevron-right" width={5} height={5} />}
              p={0}
            >
              <UiText
                style={{
                  fontWeight: 600,
                  fontSize: '14px',
                  lineHeight: '24px',
                  color: 'rgba(21, 25, 32, 0.5)',
                }}
              >
                Back
              </UiText>
            </UiButton>
          </Row>
          <PopupSorry />
        </>
      ) : (
        <>
          <BackAndNextSlide firstSlide onNextSlide={handleNextSlide} isActiveNextSlide={isNextSlide} />
          <PopupLayout>
            <Row alignItems="center" mb={8}>
              <Row flex={1} justifyContent="flex-start">
                <UiText style={{fontSize: '20px', fontWeight: 600, lineHeight: '30px'}}>Medical Questions</UiText>
              </Row>
            </Row>

            <UiFormControl isRequired>
              <UiFormLabel style={{fontSize: '16px', fontWeight: 700, lineHeight: '24px'}} mb={8}>
                As your purchasing prescription medication, please confirm your date of birth.
              </UiFormLabel>
              <UiBox>
                <TitlePopup>Date of birth</TitlePopup>
                <AppDatePicker
                  placeholderText={RESERVATION_DATE_FORMAT}
                  selected={
                    inputs.dateOfBirth && moment(inputs?.dateOfBirth).isValid() === true
                      ? moment(inputs?.dateOfBirth)
                      : undefined
                  }
                  onChange={value => {
                    setInputs({dateOfBirth: formatDate(value)});
                  }}
                  dropdownMode="select"
                  customInput={
                    <DatePickerCustomInput fontWeight="400" height="42px" bg="surfaceLight" borderRadius={4} />
                  }
                  peekNextMonth
                  showYearDropdown
                  showMonthDropdown
                  maxDate={moment().toDate()}
                  dateFormat="dd-MM-yyyy"
                />
              </UiBox>
              <UiBox>
                <TitlePopup>Sex</TitlePopup>
                <CustomDropdown
                  className="swiper-no-swiping"
                  onChange={value => setInputs({sex: value?.value})}
                  options={SEX_OPTIONS}
                  defaultItem={SEX_OPTIONS.find(i => i.value === inputs.sex)}
                  selectedFontWeight="600"
                  chakraStyles={chakraStyles}
                />
              </UiBox>
            </UiFormControl>
          </PopupLayout>
        </>
      )}
    </>
  );
};
